import React from "react";
import Dropzone from "react-dropzone";
import api from "../../api";
import "./Upload.scss";
import Button from "../button";
import { MediaFile } from "../../core";
import { toastr } from "react-redux-toastr";
import { DownloadLink } from "../render-file/DownloadLink";

interface CustomProps {
  name: string;
  id: string;
  onFileChosen?: (file?: MediaFile) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  accept?: string[];
  value?: string;
  file?: MediaFile;
}

const DragDropUploadSingle: React.SFC<CustomProps> = (props: CustomProps) => {
  const [imageFile, setImage] = React.useState(props.value);
  const [uploadedFile, setFile] = React.useState(props.file);
  const [progress, setProgress] = React.useState(0);
  const [isUploading, setUploading] = React.useState(false);
  const handleDrop = async (selectedFiles: File[]) => {
    const acceptedTypes =
      typeof props.accept === "string"
        ? [props.accept]
        : props.accept || [".pdf"];
    if (selectedFiles && selectedFiles.length === 0) {
      toastr.error(
        "Error",
        `Unsupported file type - Only ${acceptedTypes.join(
          ", "
        )} files are accepted`,
        {
          timeOut: 5000,
        }
      );
    }
    if (selectedFiles && selectedFiles.length > 0) {
      setUploading(true);
      const file = selectedFiles[0];

      const size = file.size / (1024 * 1024);
      const fileType = `.${file.name.split(".").pop()}`;
      if (!acceptedTypes.includes(fileType)) {
        toastr.error(
          "Error",
          `Unsupported file type - Only ${acceptedTypes.join(
            ", "
          )} files are accepted`,
          {
            timeOut: 5000, // 5000 milliseconds (5 seconds)
          }
        );
      } else if (size <= 50) {
        setImage(URL.createObjectURL(file));
        const uploadedResponse = await api.upload.uploadPublicFile(
          file,
          onProgress
        );
        if (props.onFileChosen) {
          props.onFileChosen(uploadedResponse?.data);
        }
        setFile(uploadedResponse?.data);
      } else {
        toastr.error(
          "Error",
          "File size limit exceeded. Please choose a file that is smaller than 50 MB ."
        );
      }

      setUploading(false);
    }
  };

  const onProgress = (event: ProgressEvent) => {
    const progressPercentage = event.loaded / event.total;
    setProgress(progressPercentage);
  };

  const closeFile = () => {
    setUploading(false);
    if (props.onFileChosen) {
      props.onFileChosen();
    }
    setImage("");
  };

  const isChosen = !!imageFile;

  if (!isChosen) {
    return (
      <>
        <Dropzone
          onDrop={handleDrop}
          multiple={false}
          accept={props?.accept ? props?.accept : ".pdf"}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps({
                className: "drag-drop-file",
              })}
            >
              <input {...getInputProps()} className="d-none" />
              <label
                htmlFor="upload"
                className="position-relative upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center"
              >
                <span className="d-block">
                  <i className="icon-upload font-xxl"></i>
                  <span className="default-upload-label">
                    <span className="d-block font-weight-semibold mt-2 py-1">
                      {props.title}
                    </span>
                    <span className="d-block font-sm font-weight-normal text-gray-500">
                      Drag file here or click to browse
                    </span>
                  </span>
                </span>
              </label>
            </div>
          )}
        </Dropzone>
      </>
    );
  } else if (isUploading) {
    return (
      <div className="position-relative  upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center">
        <div
          className="uploadfile__progress-bar"
          style={{ width: `${(progress || 0) * 100}%` }}
        ></div>
        <span className="d-block">
          <i className="icon-upload font-xxl"></i>
          <span className="default-upload-label">
            <span className="d-block font-weight-semibold mt-2 py-1">
              {props.title}
            </span>
            <span className="d-block text-gray-500 text-decoration-underline">
              Drag the file here. Or Click here
            </span>
          </span>
        </span>
      </div>
    );
  } else if (imageFile) {
    return (
      <div className="position-relative  upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center">
        <div
          className="uploadfile__progress-bar"
          style={{ width: `${(progress || 0) * 100}%` }}
        ></div>
        {uploadedFile ? (
          <span className="d-block">
            {/* <i className="icon-upload font-xxl"></i> */}
            <span className="default-upload-label">
              <span className="d-block font-weight-semibold mt-2 py-1">
                <DownloadLink
                  file={uploadedFile}
                  target="_blank"
                  className="btn-link"
                >
                  {uploadedFile?.originalname}
                </DownloadLink>
              </span>
            </span>
          </span>
        ) : (
          <span className="d-block">
            <i className="icon-upload font-xxl"></i>
            <span className="default-upload-label">
              <span className="d-block font-weight-semibold mt-2 py-1">
                {props.title}
              </span>
              <span className="d-block text-gray-500 text-decoration-underline">
                Drag the file here. Or Click here
              </span>
            </span>
          </span>
        )}

        {!props.disabled && (
          <Button
            type="button"
            btnlink
            onClick={closeFile}
            className="uploadfile__closes"
          >
            <span className="uploadfile__close">
              <i className="icon-close uploadfile__close-icon"></i>
            </span>
          </Button>
        )}
      </div>
    );
  }
  return <div></div>;
};

export default DragDropUploadSingle;
